import { getKeys } from '@shared/utils/object';

type LowercaseValues<Values extends string[]> = Lowercase<Values[number]>[];

export function createFeatureFlagConfig<Key extends string, Values extends boolean, DefaultValueType>(
  key: Key,
  values: boolean,
  defaultValue?: DefaultValueType
): {
  queryParam: `feature.${Lowercase<Key>}`;
  supportedValues: boolean;
  defaultValue: DefaultValueType;
  type: 'config';
};

export function createFeatureFlagConfig<Key extends string, Values extends string[]>(
  key: Key,
  values: readonly [...Values],
  defaultValue?: Values[number] | undefined
): { queryParam: `feature.${Lowercase<Key>}`; supportedValues: readonly [...LowercaseValues<Values>]; defaultValue: Values[number]; type: 'config' };

export function createFeatureFlagConfig<Key extends string, Values extends unknown, DefaultValueType>(key: Lowercase<Key>, values: Values, defaultValue?: DefaultValueType) {
  return {
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: Array.isArray(values) ? values.map(val => val.toLowerCase()) : values,
    defaultValue,
    type: 'config'
  } as const;
}

export function createBooleanFeatureFlag<Key extends string>(key: Key, defaultValue?: true) {
  return {
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: [true, false],
    defaultValue: defaultValue === true,
    type: 'boolean'
  } as { queryParam: `feature.${Lowercase<Key>}`; defaultValue: true | false; supportedValues: [true, false]; type: 'boolean' };
}

//---------------------------------------------
// Add new features + supported values
export const FEATURE_FLAG_VALUES = {
  layout: createFeatureFlagConfig('layout', ['aloha', 'brannan']),
  guestsitetarget: createFeatureFlagConfig('guestsitetarget', ['bliss', 'charm']),
  nossr: createBooleanFeatureFlag('nossr'),
  joyWeb: createBooleanFeatureFlag('joy-web', true),
  pseudoLocalization: createFeatureFlagConfig('pseudoloc', ['true', 'all']),
  addCashGift: createBooleanFeatureFlag('addcashgift'),
  registryGiftTracking: createBooleanFeatureFlag('registryGiftTracking'),
  bookmarkletPopup: createBooleanFeatureFlag('bookmarkletPopup'),
  productPromoFeed: createBooleanFeatureFlag('productPromoFeed'),
  externalRegistry: createBooleanFeatureFlag('externalRegistry'),
  enableRegistryPasswordUnlock: createBooleanFeatureFlag('enableRegistryPasswordUnlock'),
  enablecurationsaddallbutton: createBooleanFeatureFlag('enablecurationsaddallbutton'),
  enablePublicShop: createBooleanFeatureFlag('enablepublicshop', true),
  disableRegistryPhotoUpload: createBooleanFeatureFlag('disableregistryphotoupload'),
  enableSsr: createBooleanFeatureFlag('enablessr'),
  enableCurationHero: createBooleanFeatureFlag('enableCurationHero'),
  enableCurationHeroVideo: createBooleanFeatureFlag('enableCurationHeroVideo'),
  giftReservation: createFeatureFlagConfig('giftReservation', ['true', 'r3', 'r4']),
  compactBuildPage: createBooleanFeatureFlag('compactBuildPage'),
  enableProductColorSwatch: createBooleanFeatureFlag('enableProductColorSwatch'),
  enableCountryDropdown: createBooleanFeatureFlag('enableCountryDropdown'),
  enableCatalogMegaNav: createBooleanFeatureFlag('enableCatalogMegaNav', true),
  tileColorSwatchInteraction: createFeatureFlagConfig('tileColorSwatchInteraction', ['hover', 'click', 'hover-n-click']),
  doRegistryOnboardingCardsReset: createBooleanFeatureFlag('doRegistryOnboardingCardsReset'),
  enableNewCurationGrid: createBooleanFeatureFlag('enableNewCurationGrid'),
  enableRegistryOnboardingSurveyDialog: createBooleanFeatureFlag('enableRegistryOnboardingSurveyDialog'),
  registryOnboardingSurvey: createFeatureFlagConfig('registryOnboardingSurvey', ['force', 'reset', 'force5']),
  enableFullstoryTracking: createBooleanFeatureFlag('enableFullstoryTracking'),
  enableCatalogSearch: createBooleanFeatureFlag('enableCatalogSearch', true),
  enableCatalogFilters: createBooleanFeatureFlag('enableCatalogFilters', true),
  enableRobustPdpV2: createBooleanFeatureFlag('enableRobustPdpV2', true),
  enableRegistryTransfer: createBooleanFeatureFlag('enableRegistryTransfer', true),
  enableCreateWeddingProgressBar: createBooleanFeatureFlag('enableCreateWeddingProgressbar'),
  enableClearCartFeature: createBooleanFeatureFlag('enableClearCartFeature'),
  enableProductVariantPill: createBooleanFeatureFlag('enableProductVariantPill', true),
  enableEnhancedCatalogQuickAdd: createBooleanFeatureFlag('enableEnhancedCatalogQuickAdd'),
  enableShopHomeV1: createBooleanFeatureFlag('enableShopHomeV1', true),
  enableProductShelfCarousel: createBooleanFeatureFlag('enableProductShelfCarousel', true),
  enableHaveYouPurchasedModal: createBooleanFeatureFlag('enableHaveYouPurchasedModal', true),
  enableHaveYouPurchasedModalExternalLinkClick: createBooleanFeatureFlag('enableHaveYouPurchasedModalExternalLinkClick', true),
  enableProductTileV2: createBooleanFeatureFlag('enableProductTileV2', true),
  enableCashFundCheckoutV2: createBooleanFeatureFlag('enableCashFundCheckoutV2', true),
  enableLoadingStyleApplicator: createBooleanFeatureFlag('enableLoadingStyleApplicator'),
  enableAddressVerificationModal: createBooleanFeatureFlag('enableAddressVerificationModal'),
  enableQuickAddLandingPage: createBooleanFeatureFlag('enableQuickAddLandingPage'),
  enableAddEditGiftNote: createBooleanFeatureFlag('enableAddEditGiftNote', true),
  enableOnboardingSurveyV2: createBooleanFeatureFlag('enableOnboardingSurveyV2', true),
  enableOnboardingExperimentBypass: createBooleanFeatureFlag('enableOnboardingExperimentBypass'),
  enableCurationHomeExpert: createBooleanFeatureFlag('enableCurationHomeExpert', true),
  enableRegistryTransferV2: createBooleanFeatureFlag('enableRegistryTransferV2', true),
  enableBuyForMyselfFromPurchaseConfirmation: createBooleanFeatureFlag('enableBuyForMyselfFromPurchaseConfirmation', true),
  enableProductsPrioritization: createBooleanFeatureFlag('enableProductsPrioritization', true),
  enableShopRootV3andV4: createBooleanFeatureFlag('enableShopRootV3andV4', true),
  enableHideEmptyExternalRegistriesSection: createBooleanFeatureFlag('enableHideEmptyExternalRegistriesSection'),
  enableRandomLabelsGuestRegistryProducts: createBooleanFeatureFlag('enableRandomLabelsGuestRegistryProducts'),
  enableSplitPurchaseDialog: createBooleanFeatureFlag('enableSplitPurchaseDialog', true),
  enableProductsConsolidation: createBooleanFeatureFlag('enableProductsConsolidation', true),
  enableShippingAddressWarning: createBooleanFeatureFlag('enableShippingAddressWarning', true),
  orderTrackingFeature: createBooleanFeatureFlag('orderTrackingFeature', true),
  enableValuePropsTopBar: createBooleanFeatureFlag('enableValuePropsTopBar', true),
  enableValuePropCards: createBooleanFeatureFlag('enableValuePropCards', true),
  enableCatalogItemGroup: createBooleanFeatureFlag('enableCatalogItemGroup', true),
  enableStandaloneRegistry: createBooleanFeatureFlag('enableStandaloneRegistry'),
  enableRegistryPhotoDirectUpload: createBooleanFeatureFlag('enableRegistryPhotoDirectUpload'),
  enablePostTransferBanner: createBooleanFeatureFlag('enablePostTransferBanner', true),
  enableMFASettings: createBooleanFeatureFlag('enableMFASettings', true),
  enableBrandPages: createBooleanFeatureFlag('enableBrandPages', true),
  enableNewGuestPdpDesign: createBooleanFeatureFlag('enableNewGuestPdpDesign', true),
  enableGuestRegistryTheme: createBooleanFeatureFlag('enableGuestRegistryTheme', true),
  enablePriceRangeFilter: createBooleanFeatureFlag('enablePriceRangeFilter', true),
  enableGuestShippingAddress: createBooleanFeatureFlag('enableGuestShippingAddress'),
  enableBrandsMarquee: createBooleanFeatureFlag('enableBrandsMarquee', true),
  enableValuePropsSection: createBooleanFeatureFlag('enableValuePropsSection', true),
  enableRegistryCompletionDiscount: createBooleanFeatureFlag('enableRegistryCompletionDiscount'),
  enableHonourIntentSurvey: createBooleanFeatureFlag('enableHonourIntentSurvey'),
  enableCurationsSection: createBooleanFeatureFlag('enableCurationsSection'),
  enableRegistryRecommendation: createBooleanFeatureFlag('enableRegistryRecommendation'),
  enableAffiliateCheckoutV2: createBooleanFeatureFlag('enableAffiliateCheckoutV2'),
  enableSegmentGroupCall: createBooleanFeatureFlag('enableSegmentGroupCall'),
  enableNewExternalRegistry: createBooleanFeatureFlag('enableNewExternalRegistry'),

  /**
   * Prevent using cached SSR html.
   */
  skipSsrCache: createBooleanFeatureFlag('skipssrcache')
};
/* eslint-disable @typescript-eslint/no-explicit-any */
type DefaultState = {
  [K in keyof typeof FEATURE_FLAG_VALUES]: typeof FEATURE_FLAG_VALUES[K]['type'] extends 'boolean'
    ? boolean
    : typeof FEATURE_FLAG_VALUES[K]['supportedValues'] extends (infer T)[]
    ? T | undefined
    : any;
};

export const DEFAULT_STATE: DefaultState = getKeys(FEATURE_FLAG_VALUES).reduce((acc, curr) => {
  acc[curr] = FEATURE_FLAG_VALUES[curr].defaultValue;
  return acc;
}, {} as DefaultState);

export type FeatureFlags = typeof FEATURE_FLAG_VALUES;
